import { useEffect, useState } from 'react';
import { api, ApiError, parentalApi, type Bedtime, type Profile } from '../api';

/** Kinderslot (alleen voor de eigenaar): ouder-PIN en bedtijd per kinderprofiel. */
export function ParentalSettings() {
  const [data, setData] = useState<{ pinSet: boolean; bedtime: Record<string, Bedtime> } | null>(null);
  const [kids, setKids] = useState<Profile[]>([]);
  const [pin, setPin] = useState('');
  const [currentPin, setCurrentPin] = useState('');
  const [msg, setMsg] = useState<{ text: string; error?: boolean } | null>(null);

  useEffect(() => {
    parentalApi.get().then(setData).catch(() => setData(null)); // 403: geen eigenaar → niet tonen
    api.profiles().then((r) => setKids(r.profiles.filter((p) => p.restricted))).catch(() => setKids([]));
  }, []);

  if (!data) return null;

  const fail = (err: unknown) => setMsg({ text: err instanceof ApiError && err.message !== err.code ? err.message : 'Opslaan mislukt.', error: true });

  const savePin = async (value: string | null) => {
    try {
      const r = await parentalApi.save({ pin: value, currentPin: data.pinSet ? currentPin : undefined });
      setData(r);
      setPin('');
      setCurrentPin('');
      setMsg({ text: value ? 'Ouder-PIN opgeslagen.' : 'Ouder-PIN verwijderd.' });
    } catch (err) {
      fail(err);
    }
  };

  const saveBedtime = async (uuid: string, b: Bedtime | null) => {
    try {
      setData(await parentalApi.save({ bedtime: { [uuid]: b } }));
      setMsg({ text: 'Bedtijd opgeslagen.' });
    } catch (err) {
      fail(err);
    }
  };

  return (
    <section className="settings-block" aria-label="Kinderslot">
      <h2>Kinderslot</h2>
      <p className="subtle hint">
        Met een ouder-PIN kan een kinderprofiel niet zomaar naar een volwassen profiel wisselen. Dat los van de PIN's in Plex.
        Daarnaast stel je per kinderprofiel een bedtijd in: dan toont tumedia alleen een "Het is bedtijd"-scherm.
      </p>

      <div className="pb-grid">
        <fieldset className="pb-group">
          <legend>Ouder-PIN {data.pinSet ? '· ingesteld' : '· nog niet ingesteld'}</legend>
          {data.pinSet && (
            <label className="ed-field">Huidige PIN
              <input className="text-input" type="password" inputMode="numeric" autoComplete="off" maxLength={8} value={currentPin} onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, ''))} />
            </label>
          )}
          <label className="ed-field">{data.pinSet ? 'Nieuwe PIN' : 'PIN (4–8 cijfers)'}
            <input className="text-input" type="password" inputMode="numeric" autoComplete="new-password" maxLength={8} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} />
          </label>
          <div className="form-actions">
            <button className="btn btn-primary btn-sm" disabled={pin.length < 4 || (data.pinSet && currentPin.length < 4)} onClick={() => void savePin(pin)}>
              {data.pinSet ? 'PIN wijzigen' : 'PIN instellen'}
            </button>
            {data.pinSet && <button className="btn btn-ghost btn-sm" disabled={currentPin.length < 4} onClick={() => void savePin(null)}>PIN verwijderen</button>}
          </div>
        </fieldset>

        <fieldset className="pb-group">
          <legend>Bedtijd</legend>
          {kids.length === 0 && <span className="subtle hint">Er zijn geen kinderprofielen in je Plex Home.</span>}
          {kids.map((k) => (
            <BedtimeRow key={k.uuid} profile={k} value={data.bedtime[k.uuid] ?? null} onSave={(b) => void saveBedtime(k.uuid, b)} />
          ))}
          {!data.pinSet && kids.length > 0 && <span className="subtle hint">Tip: stel ook een ouder-PIN in, anders kun je de bedtijd niet tijdelijk opheffen.</span>}
        </fieldset>
      </div>
      {msg && <p className={msg.error ? 'error' : 'subtle'} role="status">{msg.text}</p>}
    </section>
  );
}

function BedtimeRow({ profile, value, onSave }: { profile: Profile; value: Bedtime | null; onSave: (b: Bedtime | null) => void }) {
  const [from, setFrom] = useState(value?.from ?? '20:00');
  const [to, setTo] = useState(value?.to ?? '07:00');
  const on = !!value;
  return (
    <div className="bedtime-row">
      <label className="studio-toggle">
        <span>{profile.title}</span>
        <span className="toggle"><input type="checkbox" checked={on} onChange={(e) => onSave(e.target.checked ? { from, to } : null)} /></span>
      </label>
      {on && (
        <div className="bedtime-times">
          <label>van <input type="time" value={from} onChange={(e) => setFrom(e.target.value)} onBlur={() => onSave({ from, to })} /></label>
          <label>tot <input type="time" value={to} onChange={(e) => setTo(e.target.value)} onBlur={() => onSave({ from, to })} /></label>
        </div>
      )}
    </div>
  );
}
